import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";

// SVG Icon Components
const WalletIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

const PlusIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
    />
  </svg>
);

const HistoryIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
 strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

interface WalletBalance {
  tokens: number;
  lastUpdated: Date;
}

interface Transaction {
  id: string;
  type: "topup" | "spend";
  amount: number;
  description: string;
  date: Date;
}

export default function Wallet() {
  const [balance, setBalance] = useState<WalletBalance>({ tokens: 0, lastUpdated: new Date() });
  const [topUpAmount, setTopUpAmount] = useState("");
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Mock wallet data
    setBalance({
      tokens: 5000,
      lastUpdated: new Date()
    });

    setTransactions([
      {
        id: "TXN-001",
        type: "topup",
        amount: 1000,
        description: "Top up via PromptPay",
        date: new Date(Date.now() - 3600000)
      },
      {
        id: "TXN-002",
        type: "spend",
        amount: -250,
        description: "Order #7823",
        date: new Date(Date.now() - 7200000)
      },
      {
        id: "TXN-003",
        type: "topup",
        amount: 500,
        description: "Top up via Credit Card",
        date: new Date(Date.now() - 86400000)
      }
    ]);
  }, []);

  const handleTopUp = () => {
    const amount = parseFloat(topUpAmount);
    if (amount > 0) {
      const newBalance = balance.tokens + amount;
      setBalance({
        tokens: newBalance,
        lastUpdated: new Date()
      });

      const newTransaction: Transaction = {
        id: `TXN-${Date.now()}`,
        type: "topup",
        amount: amount,
        description: "Top up via PromptPay",
        date: new Date()
      };

      setTransactions([newTransaction, ...transactions]);
      setTopUpAmount("");
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar />
      <div className="flex-1">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Token Wallet</h1>
              <p className="text-sm text-gray-500">Manage your tokens and balance</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="font-mono text-2xl font-bold text-gray-900">
                  {currentTime.toLocaleTimeString("en-GB", { hour12: false })}
                </div>
                <div className="text-sm text-gray-500">
                  {currentTime.toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Wallet Content */}
        <div className="p-6">
          {/* Balance Card */}
          <div className="card bg-white border border-gray-200 rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium mb-1">Current Balance</p>
                <p className="text-5xl font-bold text-gray-900">{balance.tokens.toLocaleString()}</p>
                <p className="text-sm text-cyan-600 mt-2">Tokens</p>
              </div>
              <div className="text-right">
                <WalletIcon className="w-5 h-5 text-cyan-600" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              Last updated: {balance.lastUpdated.toLocaleString()}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Up Card */}
            <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PlusIcon />
                Top Up Tokens
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    value={topUpAmount}
                    onChange={(e) => setTopUpAmount(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <button
                  onClick={handleTopUp}
                  disabled={!topUpAmount}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Top Up Now
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Use PromptPay or Credit Card to add tokens
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-semibold text-gray-900 mb-4">Quick Stats</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
                  <div>
                    <p className="text-sm text-gray-600">Total Top Ups</p>
                    <p className="text-xl font-bold text-gray-900">1,500</p>
                  </div>
                  <span className="text-green-500">+12.5%</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
                  <div>
                    <p className="text-sm text-gray-600">Total Spent</p>
                    <p className="text-xl font-bold text-gray-900">250</p>
                  </div>
                  <span className="text-blue-500">This month</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-xl">
                  <div>
                    <p className="text-sm text-gray-600">Pending</p>
                    <p className="text-xl font-bold text-gray-900">0</p>
                  </div>
                  <span className="text-gray-500">No pending</span>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction History */}
          <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow mt-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <HistoryIcon />
              Transaction History
            </h3>
            
            <div className="space-y-3">
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        transaction.type === "topup"
                          ? "bg-green-500 text-white"
                          : "bg-red-500 text-white"
                      }`}
                    >
                      {transaction.type === "topup" ? (
                        <PlusIcon className="w-5 h-5 text-white" />
                      ) : (
                        <WalletIcon className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {transaction.description}
                      </p>
                      <p className="text-xs text-gray-500">{transaction.id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-lg font-bold ${
                        transaction.type === "topup"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {transaction.type === "topup" ? "+" : ""}
                      {transaction.amount}
                    </p>
                    <p className="text-xs text-gray-500">
                      {transaction.date.toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
